// src/components/MAP/AnalysisView.js
import React, { 
  useCallback, 
  useEffect, 
  useRef, 
  useState,
  forwardRef, 
  useImperativeHandle 
} from "react";
import "leaflet/dist/leaflet.css";
import "./AnalysisView.css";
import VideoPlayer from "../VIDEO/VideoPlayer";
import VideoStory from "../Tabs/VideoStory";
import FilterControls from './components/FilterControls';
import { useIconFilters } from './hooks/useIconFilters';
import { useMapInitialization } from './hooks/useMapInitialization';
import { useTimeSync } from './hooks/useTimeSync';
import { useTrajectoryHighlight } from './hooks/useTrajectoryHighlight';
import { useAppStore } from "../../store/appStore";
import { useSharedState } from './hooks/useSharedState';

import { throttle } from "./utils/performance";
import videoSyncController from './controllers/VideoSyncController';
import tabSyncCoordinator from './coordinators/TabSyncCoordinator';

const AnalysisView = forwardRef(({ csvFiles, onTimeUpdate, onTrackObject, tabIndex = 1 }, ref) => {
  // 从Zustand获取全局状态
  const sharedState = useSharedState();
  const activeRobotId = useAppStore(state => state.selection.activeRobotId);
  const activeEventId = useAppStore(state => state.selection.activeEventId);
  const currentTime = useAppStore(state => state.videoState.currentTime);
  const isPlaying = useAppStore(state => state.videoState.isPlaying);
  const videoUrl = useAppStore(state => state.videoUrl);
  const timeframe = useAppStore(state => state.timeframe);
  const processingUpdate = useAppStore(state => state.metadata.processingUpdate);
  // 获取操作方法
  const updateVideoState = useAppStore(state => state.updateVideoState);
  const seekTo = useAppStore(state => state.seekTo);
  
  // 使用共享钩子初始化地图
  const {
    mapInstance,
    routes,
    setRoutes,
    timelineData,
    polylinesRef,
    backgroundPolylinesRef,
    markersRef,
    customMarkersRef,
    routeIconsManager,
    setCurrentVisibleIcons,
    clearMap,
    rebuildMap
  } = useMapInitialization("analysis-map", csvFiles, timeframe);
  
  // 时间同步钩子
  const { handleSliderChange: syncTimeChange } = useTimeSync(
    timelineData, 
    polylinesRef, 
    markersRef, 
    mapInstance, 
    routes
  );
  
  // 图标过滤钩子
  const { visibleTypes, toggleIconType } = useIconFilters(customMarkersRef);
  
  // 创建refs
  const videoRef = useRef(null);
  const isComponentMounted = useRef(true);
  
  // 使用轨迹高亮钩子
  const {
    highlightTrajectory,
    clearHighlight
  } = useTrajectoryHighlight({
    mapInstance,
    backgroundPolylinesRef,
    markersRef,
    activeRobotId,
    timeframe,
    onRobotSelect: (robotId, robotVideoUrl) => {
      // 更新全局状态，使用Zustand
      updateVideoState({ 
        robotId: robotId,
        videoUrl: robotVideoUrl
      }, 'analysisView');
    }
  });


  // 处理组件挂载/卸载
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);
  
  useEffect(() => {
    console.log('AnalysisView received props:', {
      activeRobotId, videoUrl, timeframe
    });
  }, [activeRobotId, videoUrl, timeframe]);

  // 处理弹出窗口点击事件
  useEffect(() => {
    // 为地图容器添加事件委托处理器
    const handleMapContainerClick = (e) => {
      // 检查是否点击了Check Details链接
      if (e.target.classList.contains('map-event-link') || 
          e.target.closest('.map-event-link')) {
        
        // 获取最近的事件链接元素
        const linkElement = e.target.classList.contains('map-event-link') ? 
                            e.target : e.target.closest('.map-event-link');
        
        // 获取事件数据
        const robotId = parseInt(linkElement.getAttribute('data-robot-id'), 10);
        const eventId = parseInt(linkElement.getAttribute('data-event-id'), 10);
        const startTime = parseFloat(linkElement.getAttribute('data-start-time'));
        
        console.log(`AnalysisView: Popup link clicked - Robot: ${robotId}, Event: ${eventId}, Time: ${startTime}`);
        
        // 使用handleTimeBlockClick处理点击
        handleTimeBlockClick(startTime, robotId, eventId);
      }
    };
    
    // 获取地图容器
    const mapContainer = document.getElementById('analysis-map');
    if (mapContainer) {
      mapContainer.addEventListener('click', handleMapContainerClick);
    }
    
    // 清理函数
    return () => {
      if (mapContainer) {
        mapContainer.removeEventListener('click', handleMapContainerClick);
      }
    };
  }, []);
  
  useEffect(() => {
    const unregister = videoSyncController.registerComponent(`tab_${tabIndex}`, {
      onTimeSync: (time) => {
        // Handle time sync requests
        if (videoRef.current && Math.abs(videoRef.current.currentTime - time) > 0.2) {
          videoRef.current.currentTime = time;
        }
      },
      onPlay: () => {
        if (videoRef.current && videoRef.current.paused) {
          videoRef.current.play().catch(e => console.warn('Play failed:', e));
        }
      },
      onPause: () => {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
        }
      }
    });
    
    return () => {
      if (unregister) unregister();
    };
  }, [tabIndex]);
  

  // 处理对象跟踪
  const handleTrackObject = useCallback((trackData) => {
    if (!isComponentMounted.current || !onTrackObject) return;
    onTrackObject(trackData);
  }, [onTrackObject]);

  // 处理视频时间更新 - 优化避免不必要的更新
  const handleVideoTimeUpdate = useCallback(
    throttle((time) => {
      if (!isComponentMounted.current || sharedState.processingUpdate) return;
      
      // Check if should respond to update
      if (!tabSyncCoordinator.shouldRespondToUpdate(tabIndex, 'timeUpdate')) return;
      
      // Skip minor changes
      if (Math.abs(time - sharedState.currentTime) < 0.1) return;
      
      // Notify controller of time change
      videoSyncController.updateCurrentTime(time, `tab_${tabIndex}`);
      
      // Update global state
      sharedState.updateVideoState({ time }, `tab_${tabIndex}`);
      
      // Call traditional callback
      if (onTimeUpdate) {
        onTimeUpdate(time);
      }
    }, 50), // 50ms throttle
    [sharedState.currentTime, sharedState.processingUpdate, sharedState.updateVideoState, onTimeUpdate, tabIndex]
  );

  // 视频选择处理
  const handleVideoSelect = useCallback(async (videoUrl, startTime, keepPlaying = true) => {
    if (!isComponentMounted.current || processingUpdate) return Promise.resolve();
    
    // 构建robotId
    const robotIdMatch = videoUrl.match(/video(\d+)/);
    const robotId = robotIdMatch ? parseInt(robotIdMatch[1]) : null;
    
    // 使用全局状态更新
    updateVideoState({
      robotId,
      videoUrl,
      time: startTime,
      isPlaying: keepPlaying
    }, 'analysisView');
    
    return Promise.resolve();
  }, [processingUpdate, updateVideoState]);

  // 事件块点击处理
// In AnalysisView.js
const handleTimeBlockClick = useCallback((startTime, robotId, eventId) => {
  if (!isComponentMounted.current || processingUpdate) return;
  
  // Construct the correct video URL
  const timePart = timeframe === "11AM" ? "day" : "night";
  const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
  
  console.log(`AnalysisView: Time block clicked - Time: ${startTime}, Robot: ${robotId}, Event: ${eventId}, Video: ${videoUrl}`);
  
  // Update global state with all required parameters
  updateVideoState({
    robotId: robotId,
    eventId: eventId,
    time: startTime,
    videoUrl: videoUrl // Add this line to set the video URL
  }, 'analysisView');
}, [processingUpdate, timeframe, updateVideoState]);
  // 更新图标可见性
  const updateIconsVisibility = useCallback((routeIndex, visible) => {
    if (!isComponentMounted.current) return;
    
    setRoutes((prev) => {
      // 构建新状态
      const newRoutes = {
        ...prev,
        [routeIndex]: {
          ...prev[routeIndex],
          visible: visible,
        },
      };

      // 更新路由图标
      if (routeIconsManager.current) {
        const routeIcons = routeIconsManager.current.getIconsForRoute(routeIndex);
        routeIcons.forEach((icon) => {
          if (customMarkersRef.current?.isTypeVisible(icon.type)) {
            customMarkersRef.current?.updateRouteIcons([icon], visible);
          }
        });
      }

      // 更新图层可见性
      const polyline = polylinesRef.current[routeIndex];
      const backgroundPolyline = backgroundPolylinesRef.current[routeIndex];
      const marker = markersRef.current[routeIndex];
      
      if (mapInstance.current) {
        // 轨迹线处理
        if (polyline) {
          if (visible) {
            if (!mapInstance.current.hasLayer(polyline)) {
              mapInstance.current.addLayer(polyline);
            }
          } else {
            if (mapInstance.current.hasLayer(polyline)) {
              mapInstance.current.removeLayer(polyline);
            }
          }
        }
        
        // 背景轨迹线处理
        if (backgroundPolyline) {
          if (visible) {
            if (!mapInstance.current.hasLayer(backgroundPolyline)) {
              mapInstance.current.addLayer(backgroundPolyline);
            }
          } else {
            if (mapInstance.current.hasLayer(backgroundPolyline)) {
              mapInstance.current.removeLayer(backgroundPolyline);
            }
          }
        }
        
        // 标记点处理
        if (marker) {
          if (visible) {
            if (!mapInstance.current.hasLayer(marker)) {
              mapInstance.current.addLayer(marker);
            }
          } else {
            if (mapInstance.current.hasLayer(marker)) {
              mapInstance.current.removeLayer(marker);
            }
          }
        }
      }
      
      return newRoutes;
    });
  }, [customMarkersRef, mapInstance, polylinesRef, backgroundPolylinesRef, markersRef, setRoutes]);

  // 自定义滑块变化处理函数
  const handleSliderChange = useCallback((value) => {
    if (!isComponentMounted.current || processingUpdate) return;
    
    // 更新全局状态
    updateVideoState({ time: value }, 'analysisView');
    
    // 更新地图可视化
    timelineData.forEach(({ index, routeLatLngs, timeOffsets }) => {
      // 找到最接近当前时间的路径点索引
      let closestIdx = timeOffsets.findIndex((t) => t >= value);
      if (closestIdx === -1) {
        closestIdx = timeOffsets.length - 1;
      }

      // 更新轨迹线段
      const updatedPolylinePoints = routeLatLngs.slice(0, closestIdx + 1);
      const polyline = polylinesRef.current[index];
      if (polyline) {
        polyline.setLatLngs(updatedPolylinePoints);
      }

      // 更新标记位置
      const markerLatLng = routeLatLngs[closestIdx];
      if (markersRef.current[index]) {
        markersRef.current[index].setLatLng(markerLatLng);
        
        // 确保标记可见性与路线设置一致
        const routeConfig = routes[index]; // 正确定义route变量
        if (routeConfig) {
          if (routeConfig.visible) {
            if (mapInstance.current && !mapInstance.current.hasLayer(markersRef.current[index])) {
              mapInstance.current.addLayer(markersRef.current[index]);
            }
            // 提高标记 z-index 使其置于顶层
            markersRef.current[index].setZIndexOffset(1000);
          } else {
            if (mapInstance.current && mapInstance.current.hasLayer(markersRef.current[index])) {
              mapInstance.current.removeLayer(markersRef.current[index]);
            }
          }
        }
      }

      // 更新图层可见性
      const routeConfig = routes[index]; // 正确定义route变量
      if (routeConfig) {
        if (routeConfig.visible) {
          // 添加可见图层
          if (polyline && mapInstance.current && !mapInstance.current.hasLayer(polyline)) {
            mapInstance.current.addLayer(polyline);
          }
          if (backgroundPolylinesRef.current[index] && mapInstance.current && !mapInstance.current.hasLayer(backgroundPolylinesRef.current[index])) {
            mapInstance.current.addLayer(backgroundPolylinesRef.current[index]);
          }
          
          // 调整图层顺序
          if (backgroundPolylinesRef.current[index]) backgroundPolylinesRef.current[index].bringToBack();
          if (polyline) polyline.bringToFront();
        } else {
          // 移除不可见图层
          if (polyline && mapInstance.current && mapInstance.current.hasLayer(polyline)) {
            mapInstance.current.removeLayer(polyline);
          }
          if (backgroundPolylinesRef.current[index] && mapInstance.current && mapInstance.current.hasLayer(backgroundPolylinesRef.current[index])) {
            mapInstance.current.removeLayer(backgroundPolylinesRef.current[index]);
          }
        }
      }
    });
  }, [backgroundPolylinesRef, mapInstance, markersRef, polylinesRef, routes, timelineData, updateVideoState, processingUpdate]);

  // 公开方法给父组件
  useImperativeHandle(ref, () => ({
    clearMap: () => {
      if (mapInstance.current) {
        clearMap();
      }
    },
    
    // 新增重建地图方法
    rebuildMap: () => {
      rebuildMap();
    },
    // 时间控制
    handleSliderChange,
    
    // 视频选择
    handleVideoSelect,
    
    // 事件块点击
    handleTimeBlockClick,
    
    // 图标可见性
    updateIconsVisibility,
    
    // 对象跟踪
    handleTrackObject,
    
    // 获取当前时间
    getCurrentTime: () => {
      return videoRef.current ? videoRef.current.currentTime : currentTime;
    },
    
    // 重置时间帧 - 使用全局状态
    resetTimeframe: (newTimeframe) => {
      if (!isComponentMounted.current) return;
      
      // 使用全局状态更新时间帧
      const appStore = useAppStore.getState();
      appStore.changeTimeframe(newTimeframe);
      
      // 清理自定义标记
      setCurrentVisibleIcons([]);
      if (customMarkersRef.current) {
        customMarkersRef.current.clear();
      }
    },
    
    // 禁用高亮
    disableHighlight: clearHighlight
  }));

  return (
    <div className="analysis-view-container">
      {/* 左侧面板 - 只包含视频播放器 */}
      <div className="analysis-left-panel">
        <div className="analysis-video-player">
          <VideoPlayer
            ref={videoRef}
            isExpanded={true}
            onToggleExpand={() => {}}
            videoUrl={videoUrl}
            onTimeUpdate={handleVideoTimeUpdate}
          />
        </div>
      </div>
      
      {/* 中间面板 - 视频故事/时间轴区域 */}
      <div className="analysis-middle-panel">
        <VideoStory
          robotId={activeRobotId || 1}
          onEventClick={handleTimeBlockClick}
          timeframe={timeframe}
          activeEventId={activeEventId}
          onTrackObject={handleTrackObject}
        />
      </div>
      
      {/* 右侧面板 - 地图区域 */}
      <div className="analysis-right-panel">
        <div className="analysis-map-controls">
          <FilterControls 
            visibleTypes={visibleTypes} 
            toggleIconType={toggleIconType}
          />
        </div>
        <div id="analysis-map" className="analysis-map-container"></div>
      </div>
    </div>
  );
});

export default AnalysisView;